import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Table,
  Form,
  Row,
  Col,
  Tooltip,
  Space,
  AutoComplete,
  Input,
  Divider,
  Select,
  Button,
} from 'antd';
import { isNil, isEmpty, debounce } from 'lodash';
import {
  clearSayt,
  fetchProcInstancesFundingPaginated,
  clearApptrack,
} from 'store/fundingAdmin/actions';
import { fundingAdminColumns } from './fundingAdminColumns';
import { NoResults } from 'components/layout/components';
import { ReactComponent as SearchIcon } from '../../../assets/icons/search_icon_purple.svg';
import { FormField } from 'components';
import ActionList from './components/ActionList';
import Popover from '@mui/material/Popover';
import { ReactComponent as FilterIcon } from '../../../assets/icons/filter_lines_purple.svg';
import { ReactComponent as FilterIconBold } from '../../../assets/icons/filter_lines_purple_bold.svg';
import { ReactComponent as ArrowIcon } from '../../../assets/icons/dropdown-arrow.svg';
import { fundingAdminFilters } from './fundingAdminFilters';
import { useForm } from 'antd/lib/form/Form';

const AppTrackFundingAdmin = () => {
  const [form] = useForm();
  const dispatch = useDispatch();
  const { Option } = Select;
  const groups = useSelector(state => state.user.groups);
  const results = useSelector(state => state.fundingAdmin.variables);
  const resultSize = useSelector(state => state.fundingAdmin.resultSize);
  const loadingPage = useSelector(state => state.fundingAdmin.loadingApptrack);
  const [detailedPayload, setDetailedPayload] = useState({ parameterList: [] });
  const [isFiltersVisible, setFiltersVisible] = useState(false);
  const [filterImg, setFilterImg] = useState(<FilterIcon />);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSizeFlag, setPageSizeFlag] = useState(5);
  const [saytOptions, setSaytOptions] = useState([]);
  const sayt = useSelector(state => state.fundingAdmin.sayt);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [open, setOpen] = useState(Boolean(anchorEl));
  const [selectedProcId, setSelectedProcId] = useState();
  const [applicationStatus, setApplicationStatus] = useState('');

  useEffect(() => {
    dispatch(clearApptrack());
  }, []);

  useEffect(() => {
    if (isEmpty(detailedPayload.parameterList)) {
      setCurrentPage(1);
      dispatch(
        fetchProcInstancesFundingPaginated(detailedPayload, 1, pageSizeFlag)
      );
    }
  }, [detailedPayload]);

  const handleSearch = () => {
    dispatch(
      fetchProcInstancesFundingPaginated(detailedPayload, 1, pageSizeFlag)
    );
  };

  const handlePageChange = (page, pageSize) => {
    setCurrentPage(page);
    setPageSizeFlag(pageSize);
    dispatch(
      fetchProcInstancesFundingPaginated(detailedPayload, page, pageSize)
    ); //empty param list
  };

  const toggleFilters = () => {
    setFiltersVisible(!isFiltersVisible);
    if (isFiltersVisible) {
      setFilterImg(<FilterIconBold />);
    } else {
      setFilterImg(<FilterIcon />);
    }
  };

  const overFilter = () => {
    setFilterImg(<FilterIconBold />);
  };

  const outFilter = () => {
    if (isFiltersVisible) {
      setFilterImg(<FilterIconBold />);
    } else {
      setFilterImg(<FilterIcon />);
    }
  };

  // add / replace / remove a qualifier entry
  const upsertParam = (list, qualifier, value) => {
    if (isEmpty(value)) {
      return list.filter(p => p.qualifier !== qualifier);
    }
    const idx = list.findIndex(p => p.qualifier === qualifier);
    if (idx === -1) return [...list, { qualifier, value }];
    const next = list.slice();
    next[idx] = { ...next[idx], value };
    return next;
  };

  const handleFilterChange = (name, value) => {
    setDetailedPayload(prev => ({
      ...prev,
      parameterList: upsertParam(prev.parameterList, name, value),
    }));
  };

  const initialValues = useMemo(() => {
    const map = {};
    detailedPayload.parameterList.forEach(p => {
      map[p.qualifier] = p.value;
    });
    return map;
  }, [detailedPayload.parameterList]);

  const onMoreClick = useCallback((id, record, event) => {
    setSelectedProcId(id);
    setApplicationStatus(record.status);
    setAnchorEl(event.currentTarget);
    setOpen(true);
  }, []);

  const handleClose = () => {
    setOpen(false);
    setAnchorEl(null);
  };

  const columns = useMemo(
    () => fundingAdminColumns(onMoreClick),
    [onMoreClick]
  );

  return (
    <div className="apptrack-wrapper">
      <div>
        <Popover
          id={'simple-popover'}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
        >
          <ActionList
            open={setOpen}
            id={selectedProcId}
            status={applicationStatus}
            payload={detailedPayload}
          />
        </Popover>
      </div>
      <Row>
        <Col span={24}>
          <div>
            <Tooltip placement="right" title="Expanded Filters">
              <Space
                id="filters-toggle-button"
                onClick={() => toggleFilters()}
                onMouseEnter={() => overFilter()}
                onMouseLeave={() => outFilter()}
                className="filterButton mt-3"
              >
                {filterImg}
                <span className={'filterText'}>Φίλτρα</span>
                {/* <span className={filterText}>
                    {getLocalString(
                      locales,
                      localNames.HOME_PAGE_FILTERSBUTTON
                    )}
                  </span> */}
              </Space>
            </Tooltip>

            {isFiltersVisible && (
              <div className="">
                <Form
                  form={form}
                  initialValues={initialValues}
                  trigger="onChange"
                >
                  <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
                    {fundingAdminFilters.map(field => (
                      <Col
                        key={field.id}
                        className="gutter-row"
                        span={field.span}
                      >
                        <Form.Item
                          name={field.name}
                          rules={field.rules}
                          validateFirst
                        >
                          <FormField
                            {...field}
                            onChange={event =>
                              handleFilterChange(
                                field.name,
                                field.type === 'select'
                                  ? event
                                  : event.target.value
                              )
                            }
                            options={field.options}
                          />
                        </Form.Item>
                      </Col>
                    ))}
                    {
                      <Col
                        key={'search-btn'}
                        className="gutter-row filter-search mt-1_1"
                        span={4}
                      >
                        <Button
                          type="primary"
                          className="search-btn"
                          disabled={false}
                          onClick={() => handleSearch()}
                        >
                          Αναζήτηση
                        </Button>
                      </Col>
                    }
                  </Row>
                </Form>
              </div>
            )}
          </div>

          <Divider className="filter-divider" />

          {!loadingPage && (
            <Col span={24}>
              {!isEmpty(results) && (
                <Table
                  tableLayout="fixed"
                  scroll={{ x: 1250 }}
                  id="apptrack-application-results-table"
                  className="results-table mt-3"
                  dataSource={results}
                  columns={columns}
                  rowKey="id"
                  pagination={{
                    total: resultSize,
                    current: currentPage,
                    showSizeChanger: true,
                    showTitle: true,
                    pageSizeOptions: [5, 10, 20, 50, 100, 200],
                    pageSize: pageSizeFlag,
                    onChange: (page, pageSize) => {
                      handlePageChange(page, pageSize);
                    },
                  }}
                />
              )}

              {isEmpty(results) && (
                <div style={{ width: '76rem' }}>
                  <NoResults
                    id="apptrack-application-results-no-results"
                    description={'Δε βρέθηκαν αποτελέσματα'}
                    subDescription={''}
                  />
                </div>
              )}
            </Col>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default AppTrackFundingAdmin;
